import React, { useState, useEffect } from 'react';
import { Vendor } from '@/firebase/vendorService';
import { locationService, Location } from '@/firebase/locationService';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { Search, X } from 'lucide-react';

interface VendorFormProps {
  vendor?: Vendor;
  onSubmit: (vendor: Partial<Vendor>) => Promise<void>;
  onCancel: () => void;
}

const VendorForm: React.FC<VendorFormProps> = ({ vendor, onSubmit, onCancel }) => {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const [formData, setFormData] = useState<Partial<Vendor>>({
    name: '',
    category: '',
    contact: '',
    email: '',
    phone: '',
    locationIds: [],
  });
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');

  useEffect(() => {
    loadLocations();
  }, [currentOrganization?.id, user?.id]);

  useEffect(() => {
    if (vendor) {
      setFormData({
        name: vendor.name,
        category: vendor.category || '',
        contact: vendor.contact || '',
        email: vendor.email || '',
        phone: vendor.phone || '',
        locationIds: vendor.locationIds || [],
      });
    }
  }, [vendor]);

  const loadLocations = async () => {
    if (!currentOrganization?.id) return;
    
    // Fetch user's full data from Firestore to get locationIds and role
    let userLocationIds: string[] | undefined;
    let userRole: string | undefined;
    if (user?.id) {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          userLocationIds = userData.locationIds;
          userRole = userData.role;
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }
    
    try {
      const result = await locationService.getAccessibleLocations(
        currentOrganization.id,
        user?.id,
        userRole,
        userLocationIds
      );
      if (result.success && result.data) {
        setLocations(result.data);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const handleLocationToggle = (locationId: string) => {
    setFormData(prev => {
      const currentIds = prev.locationIds || [];
      const newIds = currentIds.includes(locationId)
        ? currentIds.filter(id => id !== locationId)
        : [...currentIds, locationId];
      return { ...prev, locationIds: newIds };
    });
  };

  const filteredLocations = locations.filter(location =>
    location.name.toLowerCase().includes(locationSearchTerm.toLowerCase())
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
  };

  const vendorCategories = [
    'Fire Protection',
    'Elevator',
    'Maintenance',
    'Security',
    'HVAC',
    'Plumbing',
    'Electrical',
    'Landscaping',
    'Cleaning',
    'Catering',
    'Insurance',
    'Legal',
    'Accounting',
    'IT Services',
    'Other'
  ];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-[120] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-800">
            {vendor ? 'Edit Vendor' : 'Add New Vendor'}
          </h3>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
            &times;
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Vendor Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vendor Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md"
              placeholder="e.g., ABC Fire Protection"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md"
            >
              <option value="">Select a category...</option>
              {vendorCategories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          {/* Contact Person */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact Person
            </label>
            <input
              type="text"
              value={formData.contact}
              onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md"
              placeholder="e.g., John Smith"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md"
              placeholder="e.g., contact@vendor.com"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md"
              placeholder="e.g., (555) 123-4567"
            />
          </div>

          {/* Locations */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Locations
            </label>
            <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
              {/* Search */}
              <div className="relative mb-2">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search locations..."
                  value={locationSearchTerm}
                  onChange={(e) => setLocationSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              {/* Location checkboxes */}
              {filteredLocations.length === 0 ? (
                <div className="text-sm text-gray-500 text-center py-2">
                  {locationSearchTerm ? 'No locations found' : 'No locations available'}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredLocations.map((location) => (
                    <label
                      key={location.id}
                      className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={(formData.locationIds || []).includes(location.id || '')}
                        onChange={() => handleLocationToggle(location.id || '')}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">{location.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            {formData.locationIds && formData.locationIds.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {formData.locationIds.map(locationId => {
                  const location = locations.find(l => l.id === locationId);
                  if (!location) return null;
                  return (
                    <span
                      key={locationId}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full"
                    >
                      {location.name}
                      <button
                        type="button"
                        onClick={() => handleLocationToggle(locationId)}
                        className="hover:text-purple-900"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onCancel} className="btn-secondary px-5 py-2">
              Cancel
            </button>
            <button type="submit" className="btn-primary px-5 py-2">
              {vendor ? 'Update Vendor' : 'Add Vendor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VendorForm;

